import { __awaiter } from "tslib";
import * as server from "Shared/server";
import clone from "clone";
import { replaceState } from "Shared/history";
import { resolveComponentAndChildComponents, URLX } from "@avensia/nitro5-scope";
import { parseUrl, urlToString, areEqual } from "Shared/url";
import { batchActions } from "redux-batched-actions";
import * as cache from "./cache";
export const PAGE_LOAD = "PAGE_LOAD";
export const PAGE_LOAD_SUCCESS = "PAGE_LOAD_SUCCESS";
export const PAGE_LOAD_FAILURE = "PAGE_LOAD_FAILURE";
export const PAGE_LOAD_DONE = "PAGE_LOAD_DONE";
export const PAGE_LOAD_ABORTED = "PAGE_LOAD_ABORTED";
export const PAGE_UNHANDLED_ERROR = "PAGE_UNHANDLED_ERROR";
export const PAGE_UPDATE = "PAGE_UPDATE";
export const PAGE_UPDATE_SUCCESS = "PAGE_UPDATE_SUCCESS";
export const PAGE_UPDATE_FAILURE = "PAGE_UPDATE_FAILURE";
export const SHOW_PAGE_SPINNER = "SHOW_PAGE_SPINNER";
export const HIDE_PAGE_SPINNER = "HIDE_PAGE_SPINNER";
export const UPDATE_TRACKING_DATA = 'UPDATE_TRACKING_DATA';
let lastPageLoadRequest = 0;
export function unhandledError(error) {
    return {
        type: PAGE_UNHANDLED_ERROR,
        error: {
            message: error,
        },
    };
}
export function UpdateTrackingData(favoriteList) {
    return {
        type: UPDATE_TRACKING_DATA,
        favoriteList,
    };
}
const clientRoutes = {};
let hasClientRoutes = false;
function ensureSlashes(url) {
    if (!url.startsWith("/")) {
        url = "/" + url;
    }
    if (!url.endsWith("/")) {
        url = url + "/";
    }
    return url;
}
export function registerClientRoute(url, routeDescendents, data) {
    hasClientRoutes = true;
    clientRoutes[ensureSlashes(url)] = { routeDescendents, data };
}
export const togglePageSpinner = (show) => ({ type: show ? SHOW_PAGE_SPINNER : HIDE_PAGE_SPINNER });
export function updateCurrentPage(updater, shallowClone) {
    return (dispatch, getState) => {
        const page = shallowClone
            ? clone(getState().currentPage, false, 1)
            : clone(getState().currentPage);
        updater(page);
        dispatch({
            type: PAGE_UPDATE_SUCCESS,
            page,
        });
    };
}
function makeFullData(data, url) {
    return Object.assign({}, data, {
        componentName: data.componentName,
        url: url,
        isLoading: data.isLoading || false,
        showLoadingSpinner: data.showLoadingSpinner || false,
        loadFailure: data.loadFailure && {
            status: data.loadFailure.status,
            url: url,
            isShowingFullCachedPage: false,
            isShowingPartialCachedPage: false,
            response: null, // TODO I was required to add this property
        },
        unhandledError: data.unhandledError || null,
        reload: data.reload,
        isPartial: data.isPartial || false,
        scrollPosition: data.scrollPosition || 0,
        meta: {
            title: (data.meta && data.meta.title) || "",
            elements: (data.meta && data.meta.elements) || {},
        },
        shouldCache: false,
        shouldCacheInMemoryAnyway: false,
        currentTheme: null,
    });
}
function loadClientRoute(url) {
    if (!hasClientRoutes) {
        return null;
    }
    let path = ensureSlashes(url.path);
    let full = true;
    while (true) {
        const route = clientRoutes[path];
        if (route && (full || route.routeDescendents)) {
            const data = route.data;
            const urlx = new URLX(urlToString(url, true));
            const urlString = urlx.toString();
            if (data instanceof Function) {
                urlx.pathname = urlx.pathname.substr(path.length - 1) || "/";
                const result = data(urlx.toString());
                if (result) {
                    return result instanceof Promise
                        ? result.then((x) => makeFullData(x, urlString))
                        : Promise.resolve(makeFullData(result, urlString));
                }
            }
            else {
                return Promise.resolve(makeFullData(data, urlString));
            }
        }
        if (path === "/") {
            return null;
        }
        path = path.substring(0, path.lastIndexOf("/", path.length - 2) + 1);
        full = false;
    }
}
function loadClientRouteResult(dispatch, pagePromise, url, urlBeforeLoad, scrollPosition, options) {
    dispatch({
        type: PAGE_LOAD,
        url: url,
        urlBeforeLoad,
        cachedItem: null,
        scrollPosition,
        options,
    });
    return pagePromise.then((page) => {
        dispatch(batchActions([
            {
                type: PAGE_LOAD_SUCCESS,
                page,
                scrollPosition,
                options,
                url,
                urlBeforeLoad,
                isCurrentPage: true,
            },
            {
                type: PAGE_LOAD_DONE,
                serverRedirected: false,
            },
        ]));
    });
}
export function loadPage(url, options = {}, stateChangeEvent, shouldUseResponse) {
    return (dispatch, getState) => {
        const urlObject = typeof url === "string" ? parseUrl(url) : url;
        const currentRequest = (lastPageLoadRequest = Math.random());
        let scrollPosition = 0;
        if (stateChangeEvent && stateChangeEvent.state.scrollPosition) {
            scrollPosition = stateChangeEvent.state.scrollPosition;
        }
        const urlBeforeLoad = getState().currentPage.url;
        const clientRouteResult = loadClientRoute(urlObject);
        if (clientRouteResult) {
            return loadClientRouteResult(dispatch, clientRouteResult, urlToString(urlObject), urlBeforeLoad, scrollPosition, options);
        }
        const userIsGoingToNewPage = !(stateChangeEvent && stateChangeEvent.event === "pop");
        let headers;
        if (options.ticket) {
            headers = {
                [server.ESALES_TICKET_HEADER]: options.ticket,
                [server.ESALES_ACTION_HEADER]: server.ESALES_CLICK_ACTION,
            };
        }
        let loadPromise = null;
        const load = () => {
            if (loadPromise) {
                return loadPromise;
            }
            loadPromise = server.get(urlToString(urlObject, true), headers);
            return loadPromise;
        };
        // If the user is going to a new page, we start the network request early
        // because the cache can take ~50ms to respond and we don't want to wait
        // ~50ms to start the request.
        // If the user isn't going to a new page but going back, we don't want to
        // issue a new request because we should have that page cached. But if we don't
        // we load it after we know that the cache didn't give us anything.
        if (userIsGoingToNewPage) {
            load();
        }
        let serverRedirected = false;
        const urlString = urlToString(urlObject);
        return cache.get(urlString).then((cachedItem) => __awaiter(this, void 0, void 0, function* () {
            // This check is needed because we might want to ignore a cached response
            // like if you searched for something that gave no results we don't want to use
            // that response in a preview search.
            if (cachedItem && shouldUseResponse && !shouldUseResponse(cachedItem)) {
                cachedItem = null;
            }
            else if (cachedItem) {
                yield resolveComponentAndChildComponents(cachedItem);
            }
            const willLoadPage = userIsGoingToNewPage || !cachedItem || cachedItem.$cache === "partial";
            const actions = [
                {
                    type: PAGE_LOAD,
                    url: urlToString(urlObject),
                    urlBeforeLoad,
                    cachedItem,
                    scrollPosition,
                    options,
                },
            ];
            const pageLoadDone = {
                type: PAGE_LOAD_DONE,
                serverRedirected,
            };
            if (!willLoadPage) {
                actions.push(pageLoadDone);
            }
            try {
                dispatch(batchActions(actions));
            }
            catch (e) {
                if (process.env.NODE_ENV !== "production") {
                    const div = document.createElement("div");
                    div.style.position = "fixed";
                    div.style.zIndex = "999999999";
                    div.style.border = "5px solid red";
                    div.style.background = "white";
                    div.onclick = () => document.body.removeChild(div);
                    div.style.padding = "20px";
                    div.style.top = "10px";
                    div.style.left = "10px";
                    div.style.right = "10px";
                    div.style.bottom = "10px";
                    div.innerHTML =
                        '<h1 style="color:red">The page could not be rendered from a cached response</h1>' +
                            "<p>It seems that the current page can not be rendered with a cached response. This " +
                            "most likely happened because the backend has sent new data to this page and the component to render it assumes" +
                            " that it is there. If you absolutely must do this and can not check if the data is there, make sure that the next release " +
                            "increments the major version as that will clear the users cache.<br>" +
                            "The full error has been logged to the console." +
                            "<h4>Error:</h4>" +
                            "<pre>" +
                            e.message +
                            "</pre>";
                    document.body.appendChild(div);
                    console.error(e);
                }
                else {
                    // If this error occurs in production we can't really recover from it
                    // So we send it to Raygun and reload the page since that will
                    window.newrelic && window.newrelic.noticeError(e);
                    return cache.clear(urlString).then(() => {
                        window.location.reload();
                        return new Promise(() => null);
                    });
                }
            }
            // We only want to load the page from the server in the case that:
            // - The user is not going back to a page shes seen before
            // - The page we're displaying is only partially loaded
            // - We're in a loading state because we're waiting for the new page
            if (willLoadPage) {
                return load()
                    .then((r) => {
                    // This allows the server to use redirects. When the server redirects us
                    // `fetch` won't tell us that, so the url in the address bar won't reflect
                    // what we loaded. By using a custom header we can get what `fetch` won't give us.
                    // When that happens we call `replaceState` with a flag that we shouldn't give
                    // any events for it since we don't need to load that page.
                    const actualUrlHeader = "x-actual-url";
                    const isCurrentPage = currentRequest === lastPageLoadRequest;
                    if (r.headers.has(actualUrlHeader) && isCurrentPage) {
                        const actualUrl = parseUrl(r.headers.get(actualUrlHeader));
                        const currentUrl = parseUrl(window.location);
                        if (actualUrl.path !== currentUrl.path) {
                            urlObject.hiddenQuery &&
                                Object.keys(urlObject.hiddenQuery).forEach((key) => delete actualUrl.query[key]);
                            delete actualUrl.query[server.AJAX_REQUEST_QUERY_STRING];
                            replaceState(urlToString(actualUrl), {}, false);
                            serverRedirected = true;
                        }
                    }
                    if (server.serverHasDifferentVersion(r)) {
                        console.debug("Reloading the page since the server has a new version");
                        // Note that something similar to this is already done in server.tsx
                        // but that only checks for a major version change. Here we check for
                        // any version change and only clear the service worker cache. We don't
                        // unregister the SW or clear other caches, we just want the new scripts/styles
                        cache.nuke().then(() => {
                            window.location.reload();
                        });
                        // Return never resolving promise to wait for the page to reload
                        return new Promise(() => null);
                    }
                    return r.json();
                })
                    .then((json) => __awaiter(this, void 0, void 0, function* () {
                    const shouldUse = shouldUseResponse
                        ? shouldUseResponse(json)
                        : true;
                    if (!shouldUse) {
                        console.debug("Ignoring response because initiator did not want it");
                        return dispatch({
                            type: PAGE_LOAD_ABORTED,
                        });
                    }
                    yield resolveComponentAndChildComponents(json);
                    const isCurrentPage = currentRequest === lastPageLoadRequest;
                    dispatch(batchActions([
                        {
                            type: PAGE_LOAD_SUCCESS,
                            page: json,
                            scrollPosition,
                            options,
                            url: urlString,
                            urlBeforeLoad,
                            isCurrentPage,
                        },
                        {
                            type: PAGE_LOAD_DONE,
                            serverRedirected,
                        },
                    ]));
                }))
                    .catch((e) => {
                    if (process.env.NODE_ENV === "production") {
                        window.newrelic && window.newrelic.noticeError(e);
                    }
                    else {
                        console.error(e);
                    }
                    if (currentRequest !== lastPageLoadRequest) {
                        console.debug("Ignoring page load failure because another request is in-flight");
                        return Promise.resolve(null);
                    }
                    const urlHasNotChangedSinceLoad = areEqual(getState().currentPage.url, urlBeforeLoad);
                    const stillWaitingForPage = urlHasNotChangedSinceLoad &&
                        getState().currentPage.isLoading &&
                        !getState().currentPage.isPartial;
                    let retry;
                    if (stillWaitingForPage && stateChangeEvent) {
                        // If we're still waiting for the page and haven't been able to show a preview
                        // of the new page, revert the state change and make `retry` be the opposite of
                        // the revert
                        retry = stateChangeEvent.revert();
                    }
                    else {
                        // If we've managed to show something to the user on the new page but the full
                        // page failed to load, we just try to load it again
                        retry = () => loadPage(url, options, stateChangeEvent)(dispatch, getState);
                    }
                    dispatch(batchActions([
                        {
                            type: PAGE_LOAD_FAILURE,
                            error: e,
                            url: urlObject,
                            retry,
                            loadFailure: {
                                url: urlToString(urlObject),
                                status: e.status,
                                isShowingFullCachedPage: !urlHasNotChangedSinceLoad &&
                                    !getState().currentPage.isLoading &&
                                    !getState().currentPage.isPartial,
                                isShowingPartialCachedPage: !urlHasNotChangedSinceLoad &&
                                    getState().currentPage.isPartial,
                            },
                        },
                        {
                            type: PAGE_LOAD_DONE,
                            serverRedirected,
                        },
                    ]));
                    // If we're still on the same page as when we started loading, reject
                    // the load as we haven't reacted at all to it and we need to reset
                    if (stillWaitingForPage) {
                        return Promise.reject(e);
                    }
                    else {
                        return Promise.resolve(null);
                    }
                });
            }
        }));
    };
}
